import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";

import * as Fonts from "../../components/Fonts";
import Input from "../../components/Form/Input/input";
import { Select } from "../../components/Form/Input/select";
import { TextArea } from "../../components/Form/Input/textArea";
import Label from "../../components/Form/Label";
import { FieldHint, FormGroup } from "../../components/Form/styles";
import { ImageField } from "../../components/ImageField";
import SaveBar from "../../components/SaveBar";
import { SubHeader } from "../../components/SubHeader";
import ToggleSwitch from "../../components/ToggleSwitch";
import { CropProps } from "../../components/UploadImageInput";
import constants from "../../constants";
import { useAppState } from "../../contexts/App";
import { useSite } from "../../contexts/Site";
import { getPreviousPath } from "../../hooks/PreviousPath";
import { DEFAULT_SEO_LANGUAGE, SEO_LANGUAGES } from "../../model/seoLanguages";
import { Site, SiteSeo, WelcomeSection } from "../../model/site";
import { api, hasActiveSubscription } from "../../services/api";
import { keepoLanguageOf } from "../../services/i18ln/languages";
import { deepEqual } from "../../utils/objects";
import { Card, Container, SearchPreview, Section, ToggleRow } from "./styles";

const TITLE_LIMIT = 60;
const DESCRIPTION_LIMIT = 160;

// What the share image and the favicon take (the API makes the favicon's sizes from a PNG, JPEG or WebP)
const IMAGE_TYPES = "image/png,image/jpeg,image/webp";

const shareImageCrop: CropProps = {
  slice: { aspect: 1200 / 630 },
  size: { width: 1200, height: 630 },
  cropShape: "rect",
};

// Uploaded at 512 x 512: the API makes every favicon size from it (the biggest is 512, for home screens)
const faviconCrop: CropProps = {
  slice: { aspect: 1 },
  size: { width: 512, height: 512 },
  cropShape: "rect",
};

/** What the published page uses when a field isn't set: the form starts from these. */
type SeoDefaults = { title: string; description: string; language: string };

const seoDefaults = (site: Site, appLanguage: string): SeoDefaults => {
  const welcome = site.sections.find(section => section.type === "welcome") as WelcomeSection | undefined;
  return {
    title: welcome?.exibitionName?.trim() || site.title,
    description: welcome?.featuredTitle?.trim() || "",
    // The page's language follows its owner's account language
    language: keepoLanguageOf(appLanguage) === "en" ? "en-US" : DEFAULT_SEO_LANGUAGE,
  };
};

/** The saved settings with what the page uses today in the fields left unset. */
const withDefaults = (seo: SiteSeo, defaults: SeoDefaults): SiteSeo => ({
  ...seo,
  title: seo.title ?? defaults.title,
  description: seo.description ?? defaults.description,
  language: seo.language ?? defaults.language,
});

/**
 * Empty strings and values equal to what the page already uses aren't stored, so an untouched
 * Keepo keeps no `seo` noise (and keeps following its own title and texts). The language is the
 * exception once the owner picked one (`keepLanguage`): the default here follows the editor's
 * language, while the page falls back to the account's, so a choice that matches it is still kept.
 */
const cleanSeo = (seo: SiteSeo, defaults: SeoDefaults, keepLanguage = false): SiteSeo | undefined => {
  const title = seo.title?.trim();
  const description = seo.description?.trim();
  const cleaned: SiteSeo = {
    title: title && title !== defaults.title ? title : undefined,
    description: description && description !== defaults.description ? description : undefined,
    language: seo.language && (keepLanguage || seo.language !== defaults.language) ? seo.language : undefined,
    noIndex: seo.noIndex || undefined,
    shareImage: seo.shareImage || undefined,
    favicon: seo.favicon || undefined,
  };
  return Object.values(cleaned).some(value => value !== undefined)
    ? (JSON.parse(JSON.stringify(cleaned)) as SiteSeo)
    : undefined;
};

/**
 * Search and sharing settings of the Keepo (PLUS). Saving changes the published Keepo right away, but
 * never publishes the pending draft (see api.saveSeo).
 */
const SeoPage = () => {
  const { t, i18n } = useTranslation();
  const { site, setSite } = useSite();
  const { appState } = useAppState();
  const navigate = useNavigate();

  const savedSeo = useMemo(() => site.seo || {}, [site.seo]);
  const defaults = useMemo(() => seoDefaults(site, i18n.language), [site, i18n.language]);
  // The fields start with what the page shows today, not empty
  const [seo, setSeo] = useState<SiteSeo>(() => withDefaults(savedSeo, defaults));
  // The language is only stored once the owner picks one (or already had)
  const [languageTouched, setLanguageTouched] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  // The share image the API made for this Keepo: what the published page shows while the owner has none of their own
  const [generatedThumb, setGeneratedThumb] = useState<string>();
  useEffect(() => {
    let cancelled = false;
    api
      .getSites()
      .then(sites => {
        if (!cancelled) {
          setGeneratedThumb(sites.find(({ siteId }) => siteId === appState.editingSiteId)?.shareThumb);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [appState.editingSiteId]);

  if (!hasActiveSubscription(appState.editingSiteSubscription)) {
    return <Navigate to={`/subscription/features?site=${appState.editingSiteId}`} replace />;
  }

  const prefix = "pages.seo";
  const keepLanguage = languageTouched || !!savedSeo.language;
  const hasUnsavedChanges = !deepEqual(
    cleanSeo(seo, defaults, keepLanguage) || {},
    cleanSeo(savedSeo, defaults, !!savedSeo.language) || {}
  );
  const canSave = hasUnsavedChanges && !isSaving;

  const previewTitle = seo.title?.trim() || defaults.title;
  const previewUrl = `${site.url}.${constants.nameSite}`;

  const save = async () => {
    if (!canSave) {
      return;
    }
    setSaving(true);
    setErrorMessage(undefined);
    try {
      const cleaned = cleanSeo(seo, defaults, keepLanguage);
      const response = await api.saveSeo(appState.editingSiteId as string, cleaned ?? null);
      // What the API kept after sanitizing (it may differ from the form, e.g. tags stripped from the title)
      const saved: SiteSeo | undefined = response?.data?.data?.seo ?? undefined;
      setSite({ ...site, seo: saved });
      // Back to managing this Keepo (it stays the current one: the id lives in the app state, not the URL).
      // Through history when that's where we came from, so the back button doesn't reopen /seo
      if (getPreviousPath() === "/keepo") {
        navigate(-1);
      } else {
        navigate("/keepo");
      }
    } catch {
      setErrorMessage(t(`${prefix}.errors.generic`));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container>
      <SubHeader title={t(`${prefix}.header.title`)} crumbs={[]} hasUnsavedChanges={hasUnsavedChanges} />

      <Section>
        <Fonts.Label>{t(`${prefix}.sections.search`)}</Fonts.Label>
        <Card>
          <SearchPreview aria-label={t(`${prefix}.preview`)}>
            <span className="url">{previewUrl}</span>
            <span className="title">{previewTitle}</span>
            <span className={seo.description?.trim() ? "description" : "description empty"}>
              {seo.description?.trim() || t(`${prefix}.form.description.empty`)}
            </span>
          </SearchPreview>
          {/* It only approximates what search engines show */}
          <p className="preview-note">{t(`${prefix}.previewDisclaimer`)}</p>
          <FormGroup>
            <Input
              id="seoTitle"
              type="text"
              maxLength={TITLE_LIMIT}
              value={seo.title}
              placeholder={site.title}
              label={t(`${prefix}.form.title.label`)}
              hint={t(`${prefix}.form.title.hint`)}
              onChange={e => setSeo({ ...seo, title: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <TextArea
              id="seoDescription"
              name="seoDescription"
              rows={3}
              maxLength={DESCRIPTION_LIMIT}
              value={seo.description}
              placeholder={t(`${prefix}.form.description.placeholder`)}
              // @ts-ignore
              onChange={e => setSeo({ ...seo, description: e.target.value })}
            />
            <Label
              htmlFor="seoDescription"
              text={t(`${prefix}.form.description.label`)}
              maxLength={DESCRIPTION_LIMIT}
              usedLength={seo.description?.length || 0}
            />
          </FormGroup>
        </Card>
      </Section>

      <Section>
        <Fonts.Label>{t(`${prefix}.sections.language`)}</Fonts.Label>
        <Card>
          <FormGroup>
            <Select
              id="seoLanguage"
              value={seo.language || defaults.language}
              onChange={e => {
                setLanguageTouched(true);
                setSeo({ ...seo, language: e.target.value });
              }}>
              {SEO_LANGUAGES.map(language => (
                <option key={language.code} value={language.code}>
                  {language.name} · {language.code}
                </option>
              ))}
            </Select>
            <Label htmlFor="seoLanguage" text={t(`${prefix}.form.language.label`)} />
            <FieldHint>{t(`${prefix}.form.language.hint`)}</FieldHint>
          </FormGroup>
        </Card>
      </Section>

      <Section>
        <Fonts.Label>{t(`${prefix}.sections.visibility`)}</Fonts.Label>
        <Card>
          <ToggleRow>
            <div className="text">
              <h6>{t(`${prefix}.form.noIndex.label`)}</h6>
              <p className="description">{t(`${prefix}.form.noIndex.description`)}</p>
            </div>
            <ToggleSwitch
              label={t(`${prefix}.form.noIndex.label`)}
              value={!!seo.noIndex}
              setValue={noIndex => setSeo({ ...seo, noIndex })}
            />
          </ToggleRow>
        </Card>
      </Section>

      <Section>
        <Fonts.Label>{t(`${prefix}.sections.sharing`)}</Fonts.Label>
        <Card>
          <ImageField
            inset
            shape="wide"
            crop={shareImageCrop}
            accept={IMAGE_TYPES}
            title={t(`${prefix}.form.shareImage.label`)}
            hint={t(
              `${prefix}.form.shareImage.${!seo.shareImage && generatedThumb ? "generatedHint" : "recommendation"}`
            )}
            image={seo.shareImage}
            placeholderImage={generatedThumb}
            onChange={shareImage => setSeo({ ...seo, shareImage })}
          />
          <ImageField
            inset
            shape="square"
            crop={faviconCrop}
            accept={IMAGE_TYPES}
            title={t(`${prefix}.form.favicon.label`)}
            hint={t(`${prefix}.form.favicon.recommendation`)}
            image={seo.favicon}
            // without one of their own, the Keepo has Keepo's: shown, so it's clear it can be changed
            emptyIcon={<img src="/favicon-96x96.png" alt={t(`${prefix}.form.favicon.keepo`)} />}
            onChange={favicon => setSeo({ ...seo, favicon })}
          />
        </Card>
        {errorMessage && <p className="typography-p text-red-500">{errorMessage}</p>}
      </Section>

      <SaveBar sticky onSave={save} disabled={!canSave} hasUnsavedChanges={hasUnsavedChanges} />
    </Container>
  );
};

export default SeoPage;
